import React, { useEffect, useState } from 'react';
import { Calendar, FileText, Search, RefreshCw, Download } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const SuiviContrats = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const [data, setData] = useState([]);
  const [indicateurs, setIndicateurs] = useState({ cdi: 0, cdd: 0, stage: 0, formation: 0 });
  const [loading, setLoading] = useState(true);
  const [periodeDeb, setPeriodeDeb] = useState('');
  const [periodeFin, setPeriodeFin] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (periodeDeb) params.periode_deb = periodeDeb;
      if (periodeFin) params.periode_fin = periodeFin;
      if (filialeId && !isConsolidated) params.filiale_id = filialeId;
      
      const response = await http.get('/api/contrats/suivi', { params });
      setIndicateurs(response.data?.indicateurs || { cdi: 0, cdd: 0, stage: 0, formation: 0 });
      setData(Array.isArray(response.data?.contrats) ? response.data.contrats : []);
    } catch (error) {
      console.error('Erreur:', error);
      setIndicateurs({ cdi: 0, cdd: 0, stage: 0, formation: 0 });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filialeId, isConsolidated]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (periodeDeb) params.periode_deb = periodeDeb;
      if (periodeFin) params.periode_fin = periodeFin;
      if (filialeId && !isConsolidated) params.filiale_id = filialeId;

      const response = await http.get('/api/contrats/suivi/export', { 
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `suivi-contrats-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export:', error);
    }
  };

  const formatDate = (date) => {
    if (!date || date === '0000-00-00') return '---';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Suivi des contrats"
        subtitle="Indicateurs et suivi des contrats de travail"
        actions={[
          {
            label: 'Exporter Excel',
            icon: Download,
            onClick: handleExport,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchData,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
      {/* Indicateurs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">CDI</p>
              <p className="text-2xl font-bold text-slate-900">{indicateurs.cdi}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-sky-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">CDD</p>
              <p className="text-2xl font-bold text-slate-900">{indicateurs.cdd}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Stage</p>
              <p className="text-2xl font-bold text-slate-900">{indicateurs.stage}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Formation</p>
              <p className="text-2xl font-bold text-slate-900">{indicateurs.formation}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtre par période */}
      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-200">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">Date de début</label>
            <input
              type="date"
              value={periodeDeb}
              onChange={(e) => setPeriodeDeb(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">Date de fin</label>
            <input
              type="date"
              value={periodeFin}
              onChange={(e) => setPeriodeFin(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
              Rechercher
            </button>
          </div>
        </div>
        <div className="mt-3 text-sm text-sky-600">
          <strong>Le bornage s'effectue suivant la date de fin des contrats</strong>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            Chargement…
          </div>
        ) : data.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-slate-500">
            Aucun contrat trouvé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-sky-50/60">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Nom</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Titre</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Contrat actuel</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Début actuel</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Fin actuelle</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Jours restants</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Premier Stage</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Premier CDD</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {data.map((item) => (
                  <tr key={item.idpersonnel} className="hover:bg-sky-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {item.photo && item.photo !== 'default-picture.png' && item.photo !== '' ? (
                          <img
                            src={item.photo}
                            alt=""
                            className="h-8 w-8 rounded-full mr-3 object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="h-8 w-8 rounded-full bg-slate-200 mr-3 flex items-center justify-center" style={{ display: item.photo && item.photo !== 'default-picture.png' && item.photo !== '' ? 'none' : 'flex' }}>
                          <span className="text-xs font-medium text-slate-600">
                            {item.nom ? item.nom.charAt(0).toUpperCase() : '?'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {item.nom} {item.prenom}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {item.titre}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {item.libelle_contrat}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(item.date_entree_contrat)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(item.date_fin_contrat)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        item.jour < 30 ? 'bg-red-100 text-red-800' :
                        item.jour < 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {item.jour} jours
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {item.premier_stage_debut && item.premier_stage_debut !== '0000-00-00' 
                        ? `${formatDate(item.premier_stage_debut)} au ${formatDate(item.premier_stage_fin)}`
                        : <span className="text-slate-400">Aucun stage</span>
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {item.premier_cdd_debut && item.premier_cdd_debut !== '0000-00-00'
                        ? `${formatDate(item.premier_cdd_debut)} au ${formatDate(item.premier_cdd_fin)}`
                        : <span className="text-slate-400">Aucun CDD</span>
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => window.location.href = `/contrats/${item.idpersonnel}`}
                        className="text-sky-600 hover:text-sky-800"
                      >
                        Détails du contrat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default SuiviContrats;
